import React, {createContext, useCallback, useContext, useEffect, useState} from 'react'
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {logout} from "../../../actions/user";
import {saveToLocalStorage} from "../../../util/localStorageZugriff";
import "@fontsource/barlow-semi-condensed/600.css";
import "@fontsource/barlow-semi-condensed/700.css";
import "@fontsource/plus-jakarta-sans/400.css";
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/700.css";
import "../public/tournamentneu/publicTournamentNeu.css";
import "./startseite.css";
import {NAVIGATION_ITEM} from "../../../constants/navigationItems";
import LoginDialog from "../public/tournamentneu/LoginDialog";
import BereichWahlDialog from "./BereichWahlDialog";

export const SCORER_URL = "https://scorer.darthelfer.de";
export const SUPPORT_URL = "https://support.darthelfer.de";

/**
 * Bereiche im Adminbereich (Dashboard). Der Wert wird wie in DashboardSelectionArea unter "dh_dashboard" gemerkt
 * und vom Dashboard beim Öffnen gelesen.
 */
export const ADMINBEREICH_BEREICH = {
    ONLINE: "online",
    VERANSTALTER: "eventoperator",
};

const BEREICH_EVENTS = "events";

// Auswahl nach dem Anmelden über "Anmelden"
const BEREICHE = [
    {id: BEREICH_EVENTS, icon: "pi pi-bolt", titel: "Events & Livescores", text: "Events und laufende Spiele verfolgen."},
    {id: ADMINBEREICH_BEREICH.VERANSTALTER, icon: "pi pi-sitemap", titel: "Veranstalterbereich", text: "Eigene Turniere, Ligen und Ligaspieltage verwalten"},
    {id: ADMINBEREICH_BEREICH.ONLINE, icon: "pi pi-desktop", titel: "Onlinebereich", text: "An Onlineligen und -turnieren teilnehmen."},
];

// Einmalige Vormerkung der Frage nach dem Bereich nach einem Login über "Anmelden" auf der Startseite. Bewusst kein
// State: das Layout wird beim Wechsel in den Zweig für angemeldete Benutzer neu aufgebaut. Nach dem Neuladen ist sie weg.
let bereichWahlVorgemerkt = false;

const AdminbereichOeffnenContext = createContext(() => {});

/**
 * Liefert eine Funktion (bereich) => void, die direkt in den angegebenen Bereich des Adminbereichs wechselt.
 * Ist der Benutzer nicht angemeldet, öffnet sie zuerst den LoginDialog und wechselt nach erfolgreicher Anmeldung.
 * Nur innerhalb von StartseiteLayout verwendbar.
 */
export function useAdminbereichOeffnen() {
    return useContext(AdminbereichOeffnenContext);
}

/**
 * Rahmen der öffentlichen Seiten (Startseite, AGB, Impressum, Datenschutz):
 * Kopfleiste mit Veranstalter (in den Adminbereich), Anmelden (LoginDialog der Events) bzw. Abmelden und Fußzeile.
 *
 * @param startseite true nur auf der Startseite: dort folgt auf einen Login über "Anmelden" die Frage nach dem Bereich
 */
export default function StartseiteLayout({children, startseite = false}) {

    const [showLoginDialog, setShowLoginDialog] = useState(false);
    // Bereich, in den nach dem Login gewechselt wird; undefined beim Login über "Anmelden" (dann bleibt man auf der Startseite)
    const [bereichNachLogin, setBereichNachLogin] = useState(undefined);
    const isLoggedIn = useSelector(state => state.user.isLoggedIn);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {pathname} = useLocation();

    useEffect(() => {
        // beim Wechsel z.B. über die Links in der Fußzeile soll die neue Seite oben beginnen
        window.scrollTo(0, 0);
    }, [pathname]);

    const inAdminbereichWechseln = useCallback((bereich) => {
        saveToLocalStorage("dh_dashboard", bereich);
        navigate(NAVIGATION_ITEM.ADMINBEREICH.route);
    }, [navigate]);

    const adminbereichOeffnen = useCallback((bereich) => {
        if (isLoggedIn) {
            inAdminbereichWechseln(bereich);
            return;
        }
        bereichWahlVorgemerkt = false;
        setBereichNachLogin(bereich);
        setShowLoginDialog(true);
    }, [isLoggedIn, inAdminbereichWechseln]);

    const anmelden = () => {
        bereichWahlVorgemerkt = false;
        setBereichNachLogin(undefined);
        setShowLoginDialog(true);
    }

    const nachLogin = () => {
        if (bereichNachLogin) {
            inAdminbereichWechseln(bereichNachLogin);
            return;
        }
        if (!startseite) {
            // Login über "Anmelden" auf AGB, Impressum oder Datenschutz: keine Frage nach dem Bereich
            return;
        }
        // Die Frage nach dem Bereich wird erst gezeigt, wenn der Benutzer wirklich angemeldet ist (loginByToken).
        // App baut die Seite dann im Zweig für angemeldete Benutzer neu auf, daher wird sie hier nur vorgemerkt.
        bereichWahlVorgemerkt = true;
        if (pathname !== "/" && pathname !== NAVIGATION_ITEM.START.route) {
            // nicht angemeldet wird die Startseite unter jeder URL gezeigt, angemeldet nur unter / und /startseite
            navigate(NAVIGATION_ITEM.START.route, {replace: true});
        }
    }

    const [showBereichWahl, setShowBereichWahl] = useState(false);

    useEffect(() => {
        if (isLoggedIn && startseite && bereichWahlVorgemerkt) {
            bereichWahlVorgemerkt = false;
            setShowBereichWahl(true);
        }
    }, [isLoggedIn, startseite]);

    const bereichGewaehlt = (id) => {
        setShowBereichWahl(false);
        if (id === BEREICH_EVENTS) {
            navigate(NAVIGATION_ITEM.PUBLIC_TOURNAMENTS.route);
        } else {
            inAdminbereichWechseln(id);
        }
    }

    const abmelden = () => {
        dispatch(logout());
        navigate(NAVIGATION_ITEM.START.route);
    }

    return <AdminbereichOeffnenContext.Provider value={adminbereichOeffnen}>
    <div className="dhs-seite">
        <header className="dhs-topbar">
            <div className="dhs-container dhs-topbar-inhalt">
                <NavLink to={NAVIGATION_ITEM.START.route} aria-label="Zur Startseite">
                    <img className="dhs-logo" src="/images/darthelfer_quer_weiss.png" alt="darthelfer.de"/>
                </NavLink>
                <nav className="dhs-nav" aria-label="Hauptnavigation">
                    <NavLink to={NAVIGATION_ITEM.PUBLIC_TOURNAMENTS.route}>Events</NavLink>
                    <button type="button" className="dhs-nav-admin" onClick={() => adminbereichOeffnen(ADMINBEREICH_BEREICH.VERANSTALTER)}>Veranstalter</button>
                    <a href={SCORER_URL} target="_blank" rel="noreferrer">Scorer</a>
                    <a href={SUPPORT_URL} target="_blank" rel="noreferrer">Support</a>
                    {isLoggedIn
                        ? <button type="button" className="dhs-nav-login" onClick={abmelden}>
                            <i className="pi pi-sign-out" aria-hidden="true"/>Abmelden
                        </button>
                        : <button type="button" className="dhs-nav-login" onClick={anmelden}>
                            <i className="pi pi-sign-in" aria-hidden="true"/>Anmelden
                        </button>
                    }
                </nav>
            </div>
        </header>

        {/* LoginDialog der Events: er hängt sich in .dhpt-seite und bezieht dort seine Farbvariablen */}
        <div className="dhpt-seite dhs-login-dialog-ziel">
            {showLoginDialog && <LoginDialog onClose={() => setShowLoginDialog(false)} onAngemeldet={nachLogin}/>}
            {showBereichWahl && <BereichWahlDialog bereiche={BEREICHE} onWahl={bereichGewaehlt} onClose={() => setShowBereichWahl(false)}/>}
        </div>

        <main>{children}</main>

        <footer className="dhs-footer">
            <div className="dhs-container dhs-footer-inhalt">
                <div>
                    <img className="dhs-footer-logo" src="/images/darthelfer_quer_weiss.png" alt="darthelfer.de"/>
                    <div className="dhs-footer-kontakt">Fragen? support(a)darthelfer.de</div>
                </div>
                <nav className="dhs-footer-links" aria-label="Rechtliches">
                    <a href={SUPPORT_URL} target="_blank" rel="noreferrer">Supportseite</a>
                    <NavLink to={NAVIGATION_ITEM.AGB.route}>AGB</NavLink>
                    <NavLink to={NAVIGATION_ITEM.IMPRESSUM.route}>Impressum</NavLink>
                    <NavLink to={NAVIGATION_ITEM.DATENSCHUTZ.route}>Datenschutzerklärung</NavLink>
                </nav>
            </div>
        </footer>
    </div>
    </AdminbereichOeffnenContext.Provider>
}

/**
 * Textseite (AGB, Impressum, Datenschutz) im Startseiten-Layout. Die bestehenden Komponenten bleiben unverändert,
 * ihr alter Seitenkopf wird über das CSS ausgeblendet.
 */
export function StartseiteTextseite({titel, children}) {
    return <StartseiteLayout>
        <section className="dhs-seitenkopf">
            <div className="dhs-container">
                <span className="dhs-kicker"><span className="dhs-kicker-punkt"/>darthelfer.de</span>
                <h1>{titel}</h1>
            </div>
        </section>
        <div className="dhs-container dhs-textseite">
            <div className="dhs-textseite-inhalt">{children}</div>
        </div>
    </StartseiteLayout>
}
